import React, { useCallback, useContext, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import {
  DispatchManifestStopDetailStatus,
  ProbillStatus,
} from "app/data/city-delivery/dispatch-board/models";
import { Routes } from "app/route/RoutesConfig";
import { userSelector } from "slices/user/userSlice";
import type { ManifestProbillRow, ManifestStopBlock } from "../hooks/usePickupAssignmentDriversColumn";
import { useNestedExpandSync } from "../driverColumnNestExpandContext";
import { PickupAssignmentMapSyncContext } from "../pickupMapSyncContext";
import { ReactComponent as Arrow } from "images/arrow-down_gray.svg";
import "./manifestStopGroup.scss";

const stopStatusClass: Record<DispatchManifestStopDetailStatus, string> = {
  [DispatchManifestStopDetailStatus.COMPLETED]: "pa-manifest-stop__tag--completed",
  [DispatchManifestStopDetailStatus.IN_PROGRESS]: "pa-manifest-stop__tag--in-progress",
  [DispatchManifestStopDetailStatus.PENDING]: "pa-manifest-stop__tag--pending",
  [DispatchManifestStopDetailStatus.EXCEPTIONS]: "pa-manifest-stop__tag--exceptions",
};

const probillStatusClass: Record<ProbillStatus, string> = {
  [ProbillStatus.DELIVERED]: "pa-manifest-stop__probill-tag--delivered",
  [ProbillStatus.LOADED]: "pa-manifest-stop__probill-tag--loaded",
  [ProbillStatus.IN_PROGRESS]: "pa-manifest-stop__probill-tag--in-progress",
  [ProbillStatus.PENDING]: "pa-manifest-stop__probill-tag--pending",
  [ProbillStatus.EXCEPTIONS]: "pa-manifest-stop__probill-tag--exceptions",
};

function titleCaseWords(s: string): string {
  return s
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function stopStatusLabel(s: DispatchManifestStopDetailStatus): string {
  return titleCaseWords(s);
}

function probillStatusLabel(s: ProbillStatus): string {
  return titleCaseWords(s);
}

type Props = { task: ManifestStopBlock; onStopMapFocus?: (stopId: string) => void };

export const ManifestStopGroup: React.FC<Props> = ({ task, onStopMapFocus }) => {
  const terminalId = useSelector(userSelector).activeTerminal?.id;
  const probillHref = useCallback(
    (probillNumber: number) => {
      const prefix = terminalId != null ? `/${terminalId}` : "";
      return `${prefix}${Routes.shipments.tracking}/${probillNumber}`;
    },
    [terminalId]
  );

  const mapSyncCtx = useContext(PickupAssignmentMapSyncContext);
  const [open, setOpen] = useNestedExpandSync();

  const focusStopVersion = mapSyncCtx?.focusStopVersion;
  const focusStopId = mapSyncCtx?.focusStopId;
  useLayoutEffect(() => {
    if (!focusStopId || focusStopId !== task.id) return;
    setOpen(true);
  }, [focusStopVersion, focusStopId, task.id, setOpen]);

  const deliveryStats = `${task.deliveriesCount} ${
    task.deliveriesCount === 1 ? "delivery" : "deliveries"
  } · ${task.stopProgressLabel}`;
  const deliveredCount = task.probills.filter(
    (p) => p.probillStatus === ProbillStatus.DELIVERED
  ).length;
  const totalDeliveries = task.probills.length;

  return (
    <div className="pa-manifest-stop">
      <button
        type="button"
        className={`pa-manifest-stop__head ${!open ? "pa-manifest-stop__head--single-line" : ""}`}
        onClick={() => {
          setOpen((v) => {
            if (!v) {
              onStopMapFocus?.(task.id);
            }
            return !v;
          });
        }}
        aria-expanded={open}
      >
        <Arrow
          className="pa-manifest-stop__chevron"
          width={8}
          height={12}
          color="#828282"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
        <div className="pa-manifest-stop__head-main">
          {!open ? (
            <div className="pa-manifest-stop__collapsed-row">
              <span className="pa-manifest-stop__stop-num-circle">{task.stopNumber}</span>
              <span className="pa-manifest-stop__address pa-manifest-stop__address--collapsed">
                {task.address}
              </span>
              <span className="pa-manifest-stop__stat-tags">
                <span className="pa-manifest-stop__type-tag pa-manifest-stop__type-tag--delivery">
                  <FontAwesomeIcon icon={faArrowDown} className="pa-manifest-stop__fa-icon" />
                  {`${deliveredCount}/${totalDeliveries}`}
                </span>
              </span>
            </div>
          ) : (
            <>
              <div className="pa-manifest-stop__line1">
                <span className="pa-manifest-stop__stop-num-circle">{task.stopNumber}</span>
                <span className="pa-manifest-stop__address pa-manifest-stop__address--line1">
                  {task.address}
                </span>
                <span
                  className={`pa-manifest-stop__tag ${stopStatusClass[task.stopStatus] ?? ""}`}
                >
                  {stopStatusLabel(task.stopStatus)}
                </span>
              </div>
              <div className="pa-manifest-stop__line3">
                <span className="pa-manifest-stop__type-tag pa-manifest-stop__type-tag--delivery">
                  <FontAwesomeIcon icon={faArrowDown} className="pa-manifest-stop__fa-icon" />
                  Delivery
                </span>
                <span className="pa-manifest-stop__stats">{deliveryStats}</span>
              </div>
            </>
          )}
        </div>
      </button>

      {open && (
        <ul className="pa-manifest-stop__probills">
          {task.probills.length === 0 && (
            <li className="pa-manifest-stop__probill pa-manifest-stop__probill--empty">
              No probills for this stop
            </li>
          )}
          {task.probills.map((pb) => (
            <ProbillRow key={pb.id} probill={pb} probillHref={probillHref(pb.probillNumber)} />
          ))}
        </ul>
      )}
    </div>
  );
};

function ProbillRow({ probill, probillHref }: { probill: ManifestProbillRow; probillHref: string }) {
  return (
    <li className="pa-manifest-stop__probill">
      <div className="pa-manifest-stop__probill-line">
        <span className="pa-manifest-stop__probill-label">PRO#</span>
        <a
          className="pa-manifest-stop__probill-link"
          href={probillHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {probill.probillNumber}
        </a>
      </div>
      <div className="pa-manifest-stop__probill-line">
        <span className="pa-manifest-stop__probill-label">Consignee</span>
        <span>
          {probill.consigneeName}
          {probill.consigneeNumber != null && probill.consigneeNumber > 0 ? (
            <span className="pa-manifest-stop__consignee-num"> · #{probill.consigneeNumber}</span>
          ) : null}
        </span>
      </div>
      <div className="pa-manifest-stop__probill-line">
        <span className="pa-manifest-stop__probill-label">Assigned</span>
        <span>{probill.assignedRelative}</span>
      </div>
      <div className="pa-manifest-stop__probill-line">
        <span className="pa-manifest-stop__probill-label">Status</span>
        <span
          className={`pa-manifest-stop__probill-tag ${probillStatusClass[probill.probillStatus] ?? ""}`}
        >
          {probillStatusLabel(probill.probillStatus)}
        </span>
      </div>
      <div className="pa-manifest-stop__probill-line">
        <span className="pa-manifest-stop__probill-label">Pieces / Weight</span>
        <span>
          {probill.pieces} pcs · {probill.weight} lbs
        </span>
      </div>
    </li>
  );
}
